import { useMemo } from 'react'
import { useLocation, useMatch } from 'react-router-dom'

import Icon from '@/components/ui/icon'
import { useCurrentUser } from '@/features/auth/state/auth'
import { useFetchClientByCnpjCpf } from '@/features/client/services'

import { NavItems } from '@/layouts/SidebarLayout/nav-main'
import { TypeUsers } from '@/lib/enums'
import { useTenantContext } from '@/context/tenant'
import { ROUTE_BUILDERS, ROUTES } from '@/config/routes'
import { useProductsByRegime } from '@/features/products'

export type NavGroups = {
  items: NavItems[]
  title?: string
  kind?: 'user' | 'tenant' | 'client'
}
export default function useSidebarItems() {
  const { pathname, state } = useLocation()
  const user = useCurrentUser()
  const { selectedTenant } = useTenantContext()
  const adminClientMatch = useMatch('/admin/:tenant/clientes/:cnpjcpf/*')
  const userClientMatch = useMatch('/cliente/:cnpjcpf/*')
  const routeClientId = adminClientMatch?.params.cnpjcpf || userClientMatch?.params.cnpjcpf || ''
  const routeTenantId = adminClientMatch?.params.tenant || ''
  const selectedClientFromState = state?.client ?? null
  const { data: fallbackClient } = useFetchClientByCnpjCpf(selectedClientFromState ? undefined : routeClientId)
  const currentClient = selectedClientFromState ?? fallbackClient ?? null
  const currentClientId = currentClient?.id_client || currentClient?.cnpjcpf || routeClientId
  const activeTenantId = selectedTenant?.id || routeTenantId
  const { data: products } = useProductsByRegime(currentClientId || '')

  // grupo principal (sempre com nav-items filtrados por roles)
  const filteredNavItems = useMemo<NavItems[]>(() => {
    if (!user) return []
    return navItems
      .filter(item => item.roles.includes(user.typeCargo) && item.roles.includes(user.typeUser))
      .map(item => {
        const subItems = (item.items ?? []).filter(
          i => i.roles.includes(user.typeCargo) && i.roles.includes(user.typeUser)
        )
        return { ...item, items: subItems, kind: '' }
      })
  }, [user])

  // grupo “tenant” (quando há tenant selecionado)
  const tenantItems = useMemo<NavItems[]>(() => {
    if (!user || !selectedTenant) return []
    return TenantNavItems.filter(item => item.roles.includes(user.typeCargo) && item.roles.includes(user.typeUser)).map(
      item => ({
        ...item,
        url: `/admin/${selectedTenant.id}${item.url}`,
        kind: ''
      })
    )
  }, [user, selectedTenant])

  // grupo “client”
  const clientItems = useMemo<NavItems[]>(() => {
    if (!currentClientId) return []

    // Se for ADMIN, precisamos de selectedTenant
    if (user?.typeUser === TypeUsers.ADMIN) {
      if (!activeTenantId) return []
      return ClientNavItems.filter(
        item => item.roles.includes(user.typeCargo) && item.roles.includes(user.typeUser)
      ).map(item => ({
        ...item,
        url: `${ROUTE_BUILDERS.clientDashboardAdmin(activeTenantId, currentClientId)}/${item.url}`,
        kind: ''
      }))
    }

    // Usuário normal
    return ClientNavItems.map(item => ({
      ...item,
      url: `${ROUTE_BUILDERS.clientDashboard(currentClientId)}/${item.url}`,
      kind: ''
    }))
  }, [activeTenantId, currentClientId, user?.typeCargo, user?.typeUser])

  const groups: NavGroups[] = useMemo(() => {
    const groups: NavGroups[] = [
      {
        items: filteredNavItems,
        title: user?.typeUser === TypeUsers.ADMIN ? 'Admin' : '',
        kind: 'user'
      }
    ]

    if (tenantItems.length) {
      groups.push({
        items: tenantItems,
        title: selectedTenant?.title,
        kind: 'tenant'
      })
    }

    if (clientItems.length) {
      groups.push({
        items: clientItems,
        title: currentClient?.razao_social ?? currentClient?.nome_fantasia ?? currentClientId,
        kind: 'client'
      })

      if (products) {
        groups.forEach(group => {
          if (group.kind === 'client') {
            group.items = group.items.map(item => {
              if (item.title === 'Produtos') {
                return {
                  ...item,
                  items: Object.keys(products).map(regime => ({
                    title: regime,
                    url:
                      user?.typeUser === TypeUsers.ADMIN
                        ? ROUTE_BUILDERS.clientDashboardAdminProducts(activeTenantId, currentClientId)
                        : ROUTE_BUILDERS.clientDashboardProducts(currentClientId),
                    icon: <Icon name="Library" />,
                    products: products[regime] || []
                  }))
                }
              }
              return item
            })
          }
        })
      }
    }

    return groups
  }, [activeTenantId, clientItems, currentClient, currentClientId, filteredNavItems, products, tenantItems, user?.typeCargo, user?.typeUser])

  const currentItem = navItems.find(item => item.url === pathname) ?? navItems[0]

  return {
    groups,
    currentItem
  }
}
const navItems = [
  {
    icon: <Icon name="Alert" className="mr-2 h-4 w-4 " />,
    title: 'Alertas',
    url: ROUTES.ALERTS.path,
    roles: [
      'GERENTE',
      'GESTOR',
      'PARCEIRO',
      'CEO',
      'FINANCEIRO',
      'JURIDICO',
      'ADMIN',
      'USUARIO',
      'ANALISTA'
    ]
  },
  {
    icon: <Icon name="BookUser" className="mr-2 h-4 w-4 " />,
    title: 'Clientes',
    url: ROUTES.CLIENTS.path,
    roles: [
      'GERENTE',
      'GESTOR',
      'PARCEIRO',
      'ANALISTA',
      'CEO',
      'FINANCEIRO',
      'JURIDICO',
      // 'ADMIN', TODO: apenas quando selecionado tenant
      'USUARIO'
    ]
  },
  {
    icon: <Icon name="Users" className="mr-2 h-4 w-4 " />,
    title: 'Usuários',
    url: ROUTES.ADMIN_USERS.path,
    roles: ['CEO', 'TI', 'ADMIN']
  },
  {
    icon: <Icon name="FileText" className="mr-2 h-4 w-4 " />,
    title: 'Contratos',
    url: ROUTES.ADMIN_CONTRACTS.path,
    roles: ['CEO', 'JURIDICO', 'TI', 'ADMIN']
  },
  {
    icon: <Icon name="Bank" className="mr-2 h-4 w-4 " />,
    title: 'Informação do Banco',
    url: ROUTES.ADMIN_BANK_INFO.path,
    roles: ['CEO', 'TI', 'ADMIN']
  },
  {
    icon: <Icon name="Folder" className="mr-2 h-4 w-4 " />,
    title: 'Gestão de Analista',
    url: ROUTES.ADMIN_PRODUCTS_ANALISTA.path,
    roles: ['CEO', 'ANALISTA', 'ADMIN']
  },
  {
    icon: <Icon name="Plus" className="mr-2 h-4 w-4 " />,
    title: 'Gerar Modelo de Minuta',
    url: ROUTES.GENERATE_MINUTE.path,
    roles: ['GERENTE', 'GESTOR', 'CEO', 'PARCEIRO', 'USUARIO']
  },
  {
    icon: <Icon name="PlusCircle" className="mr-2 h-4 w-4 " />,
    title: 'Cadastrar Cliente',
    url: ROUTES.REGISTER_CLIENT.path,
    roles: ['GERENTE', 'GESTOR', 'CEO', 'PARCEIRO', 'USUARIO']
  },
  {
    icon: <Icon name="FilePen" className="mr-2 h-4 w-4 " />,
    title: 'Assinatura',
    url: ROUTES.AGREEMENT_SIGNATURES.path,
    roles: ['CEO', 'USUARIO']
  },
  {
    icon: <Icon name="Users" className="mr-2 h-4 w-4" />,
    title: 'Parceiros',
    url: '',
    roles: ['GERENTE', 'CEO', 'USUARIO'],
    items: [
      {
        title: 'Cadastrar Parceiro',
        url: ROUTES.REGISTER_PARTNER.path,
        roles: ['GERENTE', 'CEO', 'USUARIO']
      },
      {
        title: 'Lista de Parceiros',
        url: ROUTES.PARTNERS.path,
        roles: ['GERENTE', 'CEO', 'USUARIO']
      }
    ]
  }
]

const TenantNavItems = [
  // {
  //   icon: <Icon name="Alert" className="mr-2 h-4 w-4 " />,
  //   title: 'Alertas',
  //   url: ROUTES.ALERTS.path,
  //   roles: ['GERENTE', 'GESTOR', 'PARCEIRO',  'CEO', 'FINANCEIRO', 'JURIDICO', 'ADMIN', 'USUARIO']
  // },
  {
    icon: <Icon name="BookUser" className="mr-2 h-4 w-4 " />,
    title: 'Clientes',
    url: ROUTES.CLIENTS.path,
    roles: [
      'GERENTE',
      'GESTOR',
      'PARCEIRO',
      'ANALISTA',
      'CEO',
      'FINANCEIRO',
      'JURIDICO',
      'ADMIN',
      'USUARIO'
    ]
  }
  // {
  //   icon: <Icon name="Money" className="mr-2 h-4 w-4" />,
  //   title: 'Opções Financeiras',
  //   roles: ['CEO', 'ADMIN'],
  //   url: ROUTES.FINANCIAL_OPTIONS.path
  // },
  // {
  //   icon: <Icon name="FileText" className="mr-2 h-4 w-4" />,
  //   title: 'Opções Fiscais',
  //   roles: ['CEO', 'ADMIN'],
  //   url: ROUTES.FISCAL_OPTIONS.path
  // }
]

const ClientNavItems = [
  {
    icon: <Icon name="Info" className="mr-2 h-4 w-4 " />,
    title: 'Informações',
    url: 'informacoes',
    roles: ['GERENTE', 'PARCEIRO', 'ANALISTA', 'CEO', 'FINANCEIRO', 'JURIDICO', 'USUARIO', 'ADMIN']
  },
  {
    icon: <Icon name="FileText" className="mr-2 h-4 w-4 " />,
    title: 'Minutas',
    url: 'lista-minutas',
    roles: ['GERENTE', 'CEO', 'JURIDICO', 'USUARIO', 'ADMIN']
  },
  {
    icon: <Icon name="FilePlus" className="mr-2 h-4 w-4 " />,
    title: 'Adicionar Produto',
    url: 'adicionar-produto',
    roles: ['GERENTE', 'CEO', 'USUARIO']
  },
  {
    icon: <Icon name="BookOpen" className="mr-2 h-4 w-4 " />,
    title: 'Produtos',
    url: 'produtos',
    roles: [
      'GERENTE',
      'GESTOR',
      'PARCEIRO',
      'ANALISTA',
      'CEO',
      'FINANCEIRO',
      'JURIDICO',
      'USUARIO',
      'ADMIN'
    ]
  }
]
